/**
 * This is a functional component used to determine if the DonateBanner should
 * be rendered or not. This is based on various things such as url path,
 * user-specific rules, and feature flags. If those requirements pass, the
 * DonateBanner is then loaded.
 */

import {compose} from "mu-lambda";
import React from "react";

import {RenderState, useRenderState} from "@khanacademy/wonder-blocks-core";
import {useFeatureIsOnWithOverride} from "@khan/analytics-growthbook";
import {Dismissible} from "@khan/component-dismissible";
import {CookieCategory, useCookies, type CookiesAPI} from "@khan/data-cookies";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";

import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";

type Props = WithUserProfileProps & WithLocationProps;

type FlagProps = {
    children: JSX.Element;
}; // injected by withFeatureFlag

// DONATED_RECENTLY_COOKIE is the cookie name used to track if a user has
// recently donated
export const DONATED_RECENTLY_COOKIE = "donatedRecently";
// DONATED_RECENTLY_EXPIRATION_DAYS is the number of days to snooze the donate banner
export const DONATED_RECENTLY_EXPIRATION_DAYS = 45;

// A logical component which renders children if the flag passes
const FlagRenderer = ({children}: FlagProps): JSX.Element | null => {
    const shouldRender = useFeatureIsOnWithOverride("donation-banner");
    return shouldRender ? children : null;
};

// A logic component for rendering the active donation ask. It checks all the
// permissions and renders the component if they pass.
const MaybeShowDonateBanner: React.FC<Props> = (props) => {
    const {profile, profileLoading, profileError, location} = props;
    const Cookies = useCookies();
    const pathname = location.pathname;

    const renderState = useRenderState();

    const shouldShowBanner = (): boolean => {
        // All the checks to see if we should show the donation banner
        // we do the least expensive checks first reduce unnecessary calls

        if (
            // We don't need to access any of the subsequent properties during
            // initial render, as we never render this banner during SSR, so we
            // can just skip all of this.
            renderState !== RenderState.Standard ||
            // hide for e2e tests and bots
            RequestInfo.isE2E ||
            RequestInfo.isBot ||
            // hide based on url path
            isOnHiddenPath() ||
            // hide on logged out about pages
            (!PermissionsInfo.isLoggedIn &&
                (pathname.startsWith("/about") ||
                    pathname === "/contribute")) ||
            // hide on logged in home page
            (PermissionsInfo.isLoggedIn && pathname === "/") ||
            // hide from India - do not remove this without updating the logic
            // in classy.ts - getClassyCampaignURL to not have an
            // india-specific check
            RequestInfo.remoteCountry === "IN"
        ) {
            return false;
        }

        // hide banner when people are actively trying to help us.
        const CROWDIN_JIPT_LOCALE = "en-pt";
        if (RequestInfo.kaLocale === CROWDIN_JIPT_LOCALE) {
            return false;
        }

        // Hide banner if the profile is loading or there are errors
        if (profileLoading || profileError) {
            return false;
        }

        // User Profile Checks
        if (profile) {
            const attributes = profile.attributes;
            const joined = new Date(profile.get("joined"));
            const now = Date.now();
            const ONE_WEEK = 6.048e8;
            if (
                // hide banner for brand new teachers.
                // See https://khanacademy.slack.com/archives/C8BFYFQ4C/p1531255960000090
                (attributes.isTeacher &&
                    joined > new Date(now - ONE_WEEK) &&
                    !attributes.hasStudents) ||
                // hide for coached learners
                attributes.hasCoach ||
                // hide for activated district users
                attributes.includesDistrictOwnedData ||
                // hide for child accounts
                attributes.isChildAccount ||
                profile.get("email")?.match(/@tetc\.org$/i)
            ) {
                return false;
            }
        }

        return true;
    };

    const isOnHiddenPath = (): boolean => {
        // hide the banner on paths which match the following
        const exactPaths = [
            "/login",
            "/login/mobile",
            "/writing-coach/login",
            "/completesignup",
            "/parentsignup",
            "/invitesignup",
            "/search",
            "/forgotpw",
            "/pwreset",
        ];
        // hide the banner on paths which start with the following
        const pathPrefixes = [
            "/signup",
            "/writing-coach/signup",
            "/join",
            // the trailing "/" is needed as we want to hide on sub-pages
            "/donate/",
            "/devadmin",
            "/districts",
            "/map-accelerator",
            "/translations",
            "/ambassador",
            "/kids",
            "/prep",
            "/test-prep",
            "/khan-labs",
            "/khanmigo/checkout",
            "/khanmigo/teacher-tools",
            "/login/clever",
        ];
        // hide the banner if these strings appear anywhere in the pathname
        // Hour of Code matches: hour-of-|hourofcode
        const anyPathMatch = /hour-of-|hourofcode|khanmigo/;

        // hide the banner if any of the following query params match the
        // values
        const urlParams = new URLSearchParams(location.search);
        if (
            urlParams.get("utm_medium") === "ad" ||
            urlParams.get("utm_medium") === "ads"
        ) {
            return true;
        }

        if (
            exactPaths.includes(location.pathname) ||
            pathPrefixes.some((path) => location.pathname.startsWith(path)) ||
            location.pathname.match(anyPathMatch)
        ) {
            return true;
        }

        return false;
    };

    if (!shouldShowBanner()) {
        return null;
    }

    const handleDismiss = (Cookies?: CookiesAPI, dismiss?: () => void) => {
        // On dismiss, snooze for x days.
        Cookies?.createCookie(
            DONATED_RECENTLY_COOKIE,
            CookieCategory.Functional,
            DONATED_RECENTLY_EXPIRATION_DAYS.toString(),
            DONATED_RECENTLY_EXPIRATION_DAYS,
        );

        // Store the dismissal in the store
        dismiss?.();
    };

    const donateBanner = (dismiss?: () => void) => {
        if (!Cookies) {
            return null;
        }

        // hide if snooze cookie exists
        if (Cookies.readCookie(DONATED_RECENTLY_COOKIE)) {
            return null;
        }

        return (
            <FlagRenderer>
                <React.Suspense fallback={null}>
                    <DonateBanner
                        onDismiss={() => handleDismiss(Cookies, dismiss)}
                    />
                </React.Suspense>
            </FlagRenderer>
        );
    };

    // If the user is logged in, we want wrap in Dismissible
    // and pass the dismiss function to store banner dismissal
    return PermissionsInfo.isLoggedIn ? (
        <Dismissible
            dismissKey={DONATED_RECENTLY_COOKIE}
            expiresDays={DONATED_RECENTLY_EXPIRATION_DAYS}
        >
            {(dismiss) => donateBanner(dismiss)}
        </Dismissible>
    ) : (
        donateBanner()
    );
};

// Async load the Donate Banner
const DonateBanner = React.lazy(
    () => import("../donate-package/donate-banner.tsx"),
);

type ExportProps = WithoutUserProfile<
    WithoutLocation<
        JSX.LibraryManagedAttributes<
            typeof MaybeShowDonateBanner,
            React.ComponentProps<typeof MaybeShowDonateBanner>
        >
    >
>;

export default compose(
    withLocation,
    withUserProfile,
)(MaybeShowDonateBanner) as React.ComponentType<ExportProps>;
